import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { PlanPickerCard } from '../../MemberCreateModal/parts/PlanPickerCard';
import { LocalErrorBox } from '../../MemberCreateModal/parts/LocalErrorBox';
import { SoftIcon } from '../../MemberCreateModal/ui/SoftIcon';
import { MembershipUsageCard } from '../../MemberCreateModal/MembershipUsageCard';
import MDInput from 'components/MDInput';
import { money } from 'sectors/pilates/members/components/paymentReceive/ui';
import { uiBus } from 'shared/api/uiBus';

function numOrNull(x) {
  if (x === '' || x == null) return null;
  const n = Number(String(x).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function ymdOrNull(dt) {
  if (!dt) return null;
  // backend ISO ise ilk 10 char yeter
  return String(dt).slice(0, 10);
}
function buildUsageFromSnapshot(snapshot) {
  const items = Array.isArray(snapshot?.items) ? snapshot.items : [];

  return items.map((it) => ({
    id: null,
    activityId: it.activityId ?? null,
    activityName: it.activityName ?? '',
    totalSessions: Number(it.sessions ?? 0),
    totalMakeUp: Number(it.makeUp ?? 0),
    totalGift: Number(it.giftCount ?? 0),
    usedSessions: 0,
    usedMakeUp: 0,
    usedGift: 0,
  }));
}

function planSignature(snapshot) {
  if (!snapshot) return '';
  return JSON.stringify({
    name: snapshot?.name || '',
    classCapacity: Number(snapshot?.classCapacity ?? 1),
    priceTry: Number(snapshot?.priceTry ?? 0),
    items: (snapshot?.items || []).map((it) => ({
      activityId: it?.activityId ?? null,
      activityName: it?.activityName ?? '',
      sessions: Number(it?.sessions ?? 0),
      makeUp: Number(it?.makeUp ?? 0),
      giftCount: Number(it?.giftCount ?? 0),
    })),
  });
}

const empty = () => ({
  planRef: '',
  customPlan: null,

  planSnapshot: null,
  planPriceTry: null,
  agreedPriceTry: '',

  startAt: null, // yyyy-MM-dd veya null
  durationMonths: 1,
  usageByItem: [],
});

function toEditForm(pkg) {
  if (!pkg) return empty();

  const source = String(pkg?.source || '').toUpperCase();
  const isCustom = source === 'CUSTOM';

  // items zaten: totalSessions/usedSessions/... şeklinde geliyor ✅
  const items = Array.isArray(pkg.items) ? pkg.items : [];

  // MembershipUsageCard -> usageByItem bekliyor (used alanları)
  // toEditForm
  const usageByItem = items.map((it) => {
    const totalGift =
      it.totalGift ?? (typeof it.giftCount === 'number' ? it.giftCount : it.gift ? 1 : 0);

    return {
      id: it.id ?? it.activityId ?? null,
      activityId: it.activityId ?? null,
      activityName: it.activityName ?? '',

      totalSessions: it.totalSessions ?? it.sessions ?? 0,
      totalMakeUp: it.totalMakeUp ?? it.makeUp ?? 0,
      totalGift,

      usedSessions: it.usedSessions ?? 0,
      usedMakeUp: it.usedMakeUp ?? 0,
      usedGift: it.usedGift ?? 0,
    };
  });

  // PlanPickerCard / MembershipUsageCard -> planSnapshot.items (total alanları)
  const snapshot = {
    name: pkg.planName || 'Paket',
    priceTry: pkg.planPriceTry ?? null,
    classCapacity: pkg.classCapacity ?? 1,
    items: items.map((it) => {
      const totalGift =
        it.totalGift ?? (typeof it.giftCount === 'number' ? it.giftCount : it.gift ? 1 : 0);

      return {
        activityId: it.activityId ?? null,
        activityName: it.activityName,
        sessions: it.totalSessions ?? it.sessions ?? 0,
        makeUp: it.totalMakeUp ?? it.makeUp ?? 0,
        giftBool: totalGift > 0,
        giftCount: totalGift,
      };
    }),
  };

  return {
    planRef: pkg.planRefId ? String(pkg.planRefId) : '',

    // ✅ CUSTOM ise PlanPickerCard’ın “custom” kısmını doldur
    customPlan: isCustom ? snapshot : null,

    // ✅ her durumda snapshot dolu olsun (UsageCard bunu kullanıyor)
    planSnapshot: snapshot,

    planPriceTry: pkg.planPriceTry ?? null,
    agreedPriceTry: pkg.agreedPriceTry ?? '',

    // endpoint zaten yyyy-MM-dd dönüyor ✅
    startAt: pkg.startAt ? String(pkg.startAt).slice(0, 10) : null,
    durationMonths: pkg.durationMonths ?? 1,

    usageByItem,
  };
}

export function PackageEditModal({ open, onClose, memberId, pkg, onUpdated }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));
  const [changePlanOpen, setChangePlanOpen] = React.useState(false);
  const initialPlanSignatureRef = React.useRef('');

  React.useEffect(() => {
    if (!open || !pkg) return;

    const next = toEditForm(pkg);

    console.log('📦 pkg:', pkg);
    console.log('📦 pkg.items:', pkg?.items);
    console.log('📦 form from pkg:', next);

    setForm(next);
    setSaving(false);
    setErr(null);
    initialPlanSignatureRef.current = planSignature(next.planSnapshot);
  }, [open, pkg]);
  const [form, setForm] = React.useState(empty());
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const set = React.useCallback((k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
  }, []);

  const canSave =
    !!pkg?.id && !!form?.planSnapshot && String(form?.planSnapshot?.name || '').trim().length > 0;

  const safeClose = () => {
    if (saving) return;
    onClose?.();
  };

  const onSave = async () => {
    if (!memberId) return setErr('memberId yok');
    if (!pkg?.id) return setErr('packageId yok');
    if (!canSave) return setErr('Paket bilgileri eksik.');

    setSaving(true);
    setErr(null);

    try {
      const usageByItem = (form.usageByItem || [])
        .filter((u) => String(u?.activityName || '').trim().length > 0)
        .map((u) => ({
          activityName: u.activityName,
          usedSessions: Number(u.usedSessions ?? 0),
          usedMakeUp: Number(u.usedMakeUp ?? 0),
          usedGift: Number(u.usedGift ?? 0),
        }));

      // ✅ BODY: sadece update request alanları
      const dto = {
        planName: form.planSnapshot?.name ?? null,
        classCapacity: Number(form.planSnapshot?.classCapacity ?? 1),
        planPriceTry: numOrNull(form.planPriceTry),
        agreedPriceTry: numOrNull(form.agreedPriceTry),
        startAt: form.startAt || null,
        durationMonths: Number(form.durationMonths ?? 1),

        // ✅ backend MemberPackageUpdateRequest.items bekliyor
        items: (form.usageByItem || [])
          .filter((u) => String(u?.activityName || '').trim().length > 0)
          .map((u) => ({
            activityName: String(u.activityName).trim(), // ✅ EKLE
            totalSessions: Number(u.totalSessions ?? 0),
            totalMakeUp: Number(u.totalMakeUp ?? 0),
            totalGift: Number(u.totalGift ?? 0),
            usedSessions: Number(u.usedSessions ?? 0),
            usedMakeUp: Number(u.usedMakeUp ?? 0),
            usedGift: Number(u.usedGift ?? 0),
          })),
      };

      console.log('🟣 [PackageEditModal] PATCH payload:', dto);

      await membersApi.updateMemberPackage(memberId, pkg.id, dto);
      uiBus.emit('toast', {
        type: 'success',
        message: 'Paket başarıyla güncellendi',
      });
      onUpdated?.();
      onClose?.();
    } catch (e) {
      const message = e?.response?.data?.message || e?.message || 'Paket güncellenemedi';

      setErr(message);

      uiBus.emit('toast', {
        type: 'error',
        message,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open || !pkg) return null;

  const headerBg = 'linear-gradient(180deg, rgba(33,150,243,.16), rgba(33,150,243,.06))';

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : safeClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth={isWide ? 'md' : 'sm'}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '22px',
          border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
          width: isMobile ? '100%' : isWide ? '980px' : '820px',
          maxWidth: isMobile ? '100%' : '95vw',
        },
      }}
    >
      <DialogTitle sx={{ p: 0, background: headerBg }}>
        <MDBox sx={{ px: 2, py: 1.6 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
              <SoftIcon icon="edit" tone="blue" />
              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  Paketi Düzenle
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                  Mevcut paketin fiyat / kullanım / başlangıç bilgilerini güncelle
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDButton
              variant="outlined"
              color="dark"
              onClick={safeClose}
              disabled={saving}
              sx={{ borderRadius: '14px' }}
            >
              Kapat
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <MDBox sx={{ display: 'grid', gap: 1.25 }}>
          <MDBox
            sx={{
              p: 1.5,
              borderRadius: '18px',
              border: `1px solid ${theme.palette.divider}`,
              background:
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
              display: 'grid',
              gap: 1,
            }}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
            >
              <MDBox>
                <MDTypography variant="button" fontWeight="bold">
                  Mevcut Paket
                </MDTypography>
              </MDBox>

              <MDButton
                variant="outlined"
                color="info"
                sx={{ borderRadius: '14px' }}
                onClick={() => setChangePlanOpen(true)}
                disabled={saving}
              >
                <Icon sx={{ mr: 0.5, fontSize: 18 }}>swap_horiz</Icon>
                Paketi Değiştir
              </MDButton>
            </MDBox>

            <MDBox>
              <MDTypography variant="h6" fontWeight="bold">
                {form?.planSnapshot?.name || '—'}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Paket: {money(form?.planPriceTry)} • Kapasite:{' '}
                {form?.planSnapshot?.classCapacity ?? 1}
              </MDTypography>
            </MDBox>

            <MDBox sx={{ display: 'grid', gap: 0.4 }}>
              {(form?.planSnapshot?.items || []).map((it, i) => (
                <MDTypography key={i} variant="caption" sx={{ color: 'text.secondary' }}>
                  • {it.activityName} — {it.sessions ?? 0} seans / {it.makeUp ?? 0} telafi /{' '}
                  {it.giftCount ?? 0} hediye
                </MDTypography>
              ))}
            </MDBox>
          </MDBox>
          <MDBox sx={{ display: 'grid', gap: 0.75 }}>
            <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              Anlaşılan Fiyat (₺)
            </MDTypography>

            <MDInput
              type="number"
              placeholder="0"
              value={form.agreedPriceTry ?? ''}
              onChange={(e) => set('agreedPriceTry', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                },
              }}
            />

            <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
              Paket fiyatı: {money(form?.planPriceTry)}
            </MDTypography>
          </MDBox>
          {form?.planSnapshot ? (
            <MembershipUsageCard
              planSnapshot={form.planSnapshot}
              usageByItem={form.usageByItem}
              onChangeUsage={(next) =>
                setForm((prev) => {
                  const keyOf = (s) =>
                    String(s || '')
                      .trim()
                      .toLowerCase();

                  const prevMap = new Map(
                    (prev.usageByItem || []).map((x) => [keyOf(x.activityName), x])
                  );

                  const merged = (next || []).map((x) => {
                    const old = prevMap.get(keyOf(x.activityName));
                    return {
                      id: old?.id ?? null,
                      activityName: x.activityName,
                      totalSessions: old?.totalSessions ?? 0,
                      totalMakeUp: old?.totalMakeUp ?? 0,
                      totalGift: old?.totalGift ?? 0,
                      usedSessions: x.usedSessions ?? 0,
                      usedMakeUp: x.usedMakeUp ?? 0,
                      usedGift: x.usedGift ?? 0,
                    };
                  });

                  return { ...prev, usageByItem: merged };
                })
              }
            />
          ) : null}

          <LocalErrorBox message={err} />
        </MDBox>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <MDButton
          variant="outlined"
          color="dark"
          sx={{ borderRadius: '14px' }}
          onClick={safeClose}
          disabled={saving}
        >
          İptal
        </MDButton>

        <MDButton
          variant="gradient"
          color="info"
          sx={{ borderRadius: '14px' }}
          disabled={!canSave || saving}
          onClick={onSave}
          loading={saving}
        >
          <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
          Kaydet
        </MDButton>
      </DialogActions>
      <Dialog
        open={changePlanOpen}
        onClose={() => setChangePlanOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '22px',
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle>Paket Değiştir</DialogTitle>

        <DialogContent>
          <PlanPickerCard
            open={changePlanOpen}
            value={form.planRef}
            onChangeValue={(v) => set('planRef', v)}
            onChangePlanPayload={(payload) => {
              if (!payload) return;

              const oldSig = initialPlanSignatureRef.current;
              const newSig = planSignature(payload);

              setForm((prev) => {
                if (oldSig === newSig) {
                  return {
                    ...prev,
                    planSnapshot: payload,
                    planPriceTry: payload?.priceTry ?? null,
                  };
                }

                return {
                  ...prev,
                  customPlan: null,
                  planSnapshot: payload,
                  planPriceTry: payload?.priceTry ?? null,
                  agreedPriceTry: payload?.priceTry ?? '',
                  usageByItem: buildUsageFromSnapshot(payload),
                };
              });
            }}
            customPlanPayload={form.customPlan}
            onChangeCustomPlanPayload={(payload) => {
              setForm((prev) => {
                if (!payload) {
                  return { ...prev, customPlan: null };
                }

                const oldSig = initialPlanSignatureRef.current;
                const newSig = planSignature(payload);

                if (oldSig === newSig) {
                  return {
                    ...prev,
                    customPlan: payload,
                    planSnapshot: payload,
                    planPriceTry: payload?.priceTry ?? null,
                  };
                }

                return {
                  ...prev,
                  customPlan: payload,
                  planSnapshot: payload,
                  planPriceTry: payload?.priceTry ?? null,
                  agreedPriceTry: payload?.priceTry ?? '',
                  usageByItem: buildUsageFromSnapshot(payload),
                };
              });
            }}
            agreedPriceTry={form.agreedPriceTry}
            onChangeAgreedPriceTry={(v) => set('agreedPriceTry', v)}
            fallbackPlanSnapshot={form.planSnapshot}
            hideAgreedPrice
          />
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="gradient"
            color="success"
            onClick={() => {
              setChangePlanOpen(false);
            }}
            sx={{ borderRadius: '14px' }}
          >
            Kaydet
          </MDButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
